import type { Breadcrumb, BrowserOptions, ErrorEvent, EventHint } from '@sentry/angular';
import { isEmptyError } from '@shared/utils/empty-error';

type TransactionEvent = Parameters<NonNullable<BrowserOptions['beforeSendTransaction']>>[0];
type SpanJson = Parameters<NonNullable<BrowserOptions['beforeSendSpan']>>[0];

/**
 * Sentry scrubbers (UNIVAPLAT-3059, HIPAA finding P0-05).
 *
 * The clinician screens render patient identifiers, risk data and message bodies, and HTTP error
 * bodies can carry the same. Everything that could hold such data is removed before an event leaves
 * the browser: request/response bodies, query strings, headers, cookies, user identity, extra data,
 * non-SDK contexts, and breadcrumb data. Mirrors SentryEventScrubber in api.cloud.univa.health.
 *
 * Fail-safe: if scrubbing throws, the event is dropped (null) rather than sent partially scrubbed.
 */

export const REDACTED = '[redacted]';

/** Breadcrumb categories dropped outright: they embed URLs, bodies, console args or DOM text. */
const DROPPED_BREADCRUMB_CATEGORIES = ['fetch', 'xhr', 'console', 'ui.click', 'ui.input'];

/** SDK-owned contexts that carry no application data. */
const ALLOWED_CONTEXTS = ['runtime', 'os', 'browser', 'device', 'trace', 'culture'];

const TEXT_PATTERNS: Array<[RegExp, string]> = [
    // JWTs and bearer tokens
    [/\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]*/g, '[token]'],
    [/\bBearer\s+[A-Za-z0-9._~+/=-]+/gi, 'Bearer [token]'],
    // Email addresses
    [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[email]'],
    // UUIDs
    [/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '[uuid]'],
    // Phone / NHS-style numbers: 9+ digits, optionally separated
    [/(?<![\w.])\+?\d(?:[\s.-]?\d){8,}(?!\w)/g, '[number]'],
    // key=value / "key": "value" pairs naming credentials or identifiers
    [
        /\b(password|passwd|secret|token|authorization|api[_-]?key|otp|nhs[_-]?number|dob|date[_-]?of[_-]?birth)\b(["']?\s*[:=]\s*["']?)[^\s,"'}&]+/gi,
        '$1$2[redacted]',
    ],
];

/** HttpErrorResponse (via Sentry.createErrorHandler) embeds the server body: drop it wholesale. */
const HTTP_BODY_PATTERN = /(Server returned code \d+ with body )(["']?)[\s\S]*$/i;

const URL_PATTERN = /https?:\/\/[^\s"'<>)]+/gi;

export function scrubText(text: string): string {
    let scrubbed = text.replace(HTTP_BODY_PATTERN, `$1${REDACTED}`);
    // Request URLs in "Http failure response for <url>: 500": strip query and mask ids.
    scrubbed = scrubbed.replace(URL_PATTERN, (url) => {
        const trailing = /[:.,;]+$/.exec(url)?.[0] ?? '';
        return scrubUrl(url.slice(0, url.length - trailing.length)) + trailing;
    });
    for (const [pattern, replacement] of TEXT_PATTERNS) {
        scrubbed = scrubbed.replace(pattern, replacement);
    }
    return scrubbed;
}

/** Mask numeric ids, emails and long opaque segments in a URL path. */
function scrubUrlPath(path: string): string {
    return path
        .split('/')
        .map((segment) => {
            if (segment === '') {
                return segment;
            }
            if (/^\d+$/.test(segment)) {
                return '{id}';
            }
            if (segment.includes('@') || segment.includes('%40')) {
                return '{email}';
            }
            if (/^[A-Za-z0-9_\-=.%]{20,}$/.test(segment)) {
                return '{token}';
            }
            return segment;
        })
        .join('/');
}

/** Strip query string and fragment, mask identifier-like path segments. */
export function scrubUrl(url: string): string {
    try {
        const parsed = new URL(url, 'http://placeholder.invalid');
        const origin = /^[a-z][a-z0-9+.-]*:\/\//i.test(url) ? parsed.origin : '';
        return origin + scrubUrlPath(parsed.pathname);
    } catch {
        return REDACTED;
    }
}

function scrubEventCommon<T extends ErrorEvent | TransactionEvent>(event: T): T {
    const request = event.request;
    if (request) {
        event.request = {
            ...(request.method ? { method: request.method } : {}),
            ...(request.url ? { url: scrubUrl(request.url) } : {}),
        };
    }

    delete event.user;
    event.extra = {};
    event.tags = {};

    if (event.contexts) {
        const contexts: Record<string, Record<string, unknown>> = {};
        for (const name of Object.keys(event.contexts)) {
            if (ALLOWED_CONTEXTS.includes(name)) {
                contexts[name] = event.contexts[name] as Record<string, unknown>;
            }
        }
        // The root span's attributes (url, route, query data) are copied here: keep ids/op/status only.
        const trace = contexts['trace'];
        if (trace) {
            delete trace['data'];
        }
        event.contexts = contexts;
    }

    if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs
            .map((crumb) => beforeBreadcrumb(crumb))
            .filter((crumb): crumb is Breadcrumb => crumb !== null);
    }

    if (event.message) {
        event.message = scrubText(event.message);
    }
    if (event.logentry) {
        const { message, params: _params, ...logRest } = event.logentry;
        event.logentry = {
            ...logRest,
            ...(message ? { message: scrubText(message) } : {}),
        };
    }
    if (event.transaction) {
        event.transaction = scrubUrlPath(event.transaction);
    }

    return event;
}

/**
 * UNIVAPLAT-3639: an EmptyError left by an HTTP error the interceptor already handled. Drops the event
 * only when every exception in it is an EmptyError; anything else is reported as before.
 */
function isInterceptorSwallowedEmptyError(event: ErrorEvent, hint?: EventHint): boolean {
    if (isEmptyError(hint?.originalException)) {
        return true;
    }
    const values = event.exception?.values ?? [];
    return values.length > 0 && values.every((exception) => isEmptyError({ name: exception.type, message: exception.value }));
}

export function beforeSend(event: ErrorEvent, hint?: EventHint): ErrorEvent | null {
    try {
        if (isInterceptorSwallowedEmptyError(event, hint)) {
            return null;
        }
        scrubEventCommon(event);

        for (const exception of event.exception?.values ?? []) {
            if (exception.value) {
                exception.value = scrubText(exception.value);
            }
            if (exception.mechanism?.data) {
                exception.mechanism.data = {};
            }
            for (const frame of exception.stacktrace?.frames ?? []) {
                delete frame.vars;
            }
        }

        return event;
    } catch {
        return null;
    }
}

export function beforeSendTransaction(event: TransactionEvent): TransactionEvent | null {
    try {
        scrubEventCommon(event);

        // Spans carry URLs, SQL-like descriptions and arbitrary data: keep op and timing only.
        for (const span of event.spans ?? []) {
            span.description = undefined;
            span.data = {};
        }

        return event;
    } catch {
        return null;
    }
}

/**
 * Runs on every span, including standalone ones (INP, web vitals) that never pass through
 * beforeSendTransaction. Standalone spans carry the raw route in the `transaction` attribute and a
 * DOM selector as their name, so keep only op and timing.
 */
export function beforeSendSpan(span: SpanJson): SpanJson {
    try {
        const transaction = span.data?.['transaction'];
        span.data = typeof transaction === 'string' ? { transaction: scrubUrlPath(transaction) } : {};
        span.description = span.description?.startsWith('/') ? scrubUrlPath(span.description) : undefined;
        return span;
    } catch {
        // Cannot drop a span by returning null; blank everything instead.
        span.data = {};
        span.description = undefined;
        return span;
    }
}

/** Drops breadcrumbs that can carry bodies/DOM text; strips data from the rest. */
export function beforeBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
    try {
        const category = breadcrumb.category ?? '';
        if (DROPPED_BREADCRUMB_CATEGORIES.includes(category) || category.startsWith('ui.')) {
            return null;
        }

        const { data: _data, message, ...rest } = breadcrumb;
        return message ? { ...rest, message: scrubText(message) } : rest;
    } catch {
        return null;
    }
}
